import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { WorksheetService } from 'src/app/features/worksheet/services/worksheet.service';
import { MoistureHistoryEvent, MoistureHistoryRow, HistoryTestChange } from 'src/app/features/worksheet/models/native-soil-worksheet.modal';
import { FormService } from 'src/app/shared/services/form.service';
import { plural } from 'src/app/shared/components/history-timeline/history-timeline.component';
import { HistoryChange } from 'src/app/shared/models/history-timeline.modal';

/**
 * Timeline of saves and assignments on the Moisture Conditioning worksheet. Each entry shows the whole worksheet as
 * it stood after the action. The rows it changed are highlighted: every moisture test with what happened to it
 * (added, removed or edited, with the previous values), plus the row's assignment and status.
 */
@Component({
    selector: 'app-moisture-conditioning-history',
    templateUrl: './moisture-conditioning-history.component.html',
    styleUrls: ['./moisture-conditioning-history.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MoistureConditioningHistoryComponent implements OnInit {
  projectId: string | null = null;
  companyName = '';
  projectName = '';
  referenceId = '';
  // Newest first: the most recent action is the leftmost point on the timeline
  events: MoistureHistoryEvent[] = [];
  selectedIndex = -1;
  isLoading = true;
  // Same order as the Moisture Conditioning worksheet table
  displayedColumns = ['sampleId', 'compactionEffort', 'depth', 'testNo', 'drySoil', 'moistureContent', 'assignedTo', 'status'];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private worksheetService: WorksheetService,
    private formService: FormService,
  ) {}

  ngOnInit(): void {
    this.projectId = this.route.snapshot.queryParamMap.get('projectId');
    if (!this.projectId) {
      this.isLoading = false;
      return;
    }

    this.formService.fetchFormData(this.projectId, 'nativesoil').subscribe({
      next: (result) => {
        this.companyName = result?.company?.name ?? '';
        this.projectName = result?.project?.projectName ?? '';
        this.referenceId = result?.project?.referenceId ?? '';
      },
    });

    this.worksheetService.getMoistureHistory(this.projectId).subscribe({
      next: (events) => {
        // The API returns newest first, which is also the timeline's left-to-right order
        this.events = (Array.isArray(events) ? events : []).map((event) => ({ ...event, changes: this.buildChanges(event) }));
        this.isLoading = false;
        // Open on the latest action (the leftmost point)
        this.selectedIndex = this.events.length > 0 ? 0 : -1;
      },
      error: () => {
        this.events = [];
        this.isLoading = false;
      },
    });
  }

  get selectedEvent(): MoistureHistoryEvent | null {
    return this.events[this.selectedIndex] ?? null;
  }

  backToWorksheet(): void {
    this.router.navigate(['moisture-conditioning-worksheet'], { queryParams: { projectId: this.projectId } });
  }

  reDirectUrl(): void {
    this.router.navigate(['dashboard']);
  }

  displayDepth(row: MoistureHistoryRow): string {
    if (row.depth === null || row.depth === undefined) {
      return '';
    }
    return row.endDepth !== null && row.endDepth !== undefined ? `${row.depth}-${row.endDepth}` : `${row.depth}`;
  }

  displayValue(value: string | number | boolean | null | undefined, isStatus = false): string {
    if (isStatus) {
      return value ? 'Completed' : 'Not completed';
    }
    return value === null || value === undefined || value === '' ? '—' : `${value}`;
  }

  // Label next to a test's number; unchanged tests get none
  testChangeLabel(change: HistoryTestChange): string {
    return change === 'Unchanged' ? '' : change;
  }

  // Timeline icons: tests added, tests removed, edits (test values and status) and assignments, each with its count
  private buildChanges(event: MoistureHistoryEvent): HistoryChange[] {
    const tests = event.rows.flatMap((row) => row.tests);
    const countTests = (change: HistoryTestChange) => tests.filter((test) => test.change === change).length;
    const added = countTests('Added');
    const removed = countTests('Removed');
    const editedTests = countTests('Edited');
    const statuses = event.rows.filter((row) => row.status.changed).length;
    const assigned = event.rows.filter((row) => row.assignedTo.changed).length;
    const firstSaves = event.rows.filter((row) => row.isFirstRecord).length;

    const changes: HistoryChange[] = [];
    if (added > 0) {
      changes.push({ kind: 'added', tooltip: `${plural(added, 'test')} added` });
    }
    if (removed > 0) {
      changes.push({ kind: 'removed', tooltip: `${plural(removed, 'test')} removed` });
    }
    const edits = [
      editedTests > 0 ? `${plural(editedTests, 'test')} edited` : '',
      statuses > 0 ? `${plural(statuses, 'status', 'statuses')} changed` : '',
      firstSaves > 0 ? `${plural(firstSaves, 'row')} saved for the first time` : '',
    ].filter(Boolean);
    if (edits.length > 0) {
      changes.push({ kind: 'edited', tooltip: edits.join(', ') });
    }
    if (assigned > 0) {
      changes.push({ kind: 'assigned', tooltip: `${plural(assigned, 'row')} assigned` });
    }
    return changes;
  }

  // Same group lines as the worksheet: dotted between efforts of a sample, thick under the last row of a sample
  isLastRowOfEffort(rows: MoistureHistoryRow[], index: number): boolean {
    const nextRow = rows[index + 1];
    return !!nextRow && nextRow.sampleId === rows[index].sampleId && nextRow.compactionEffort !== rows[index].compactionEffort;
  }

  isLastRowOfSample(rows: MoistureHistoryRow[], index: number): boolean {
    const nextRow = rows[index + 1];
    return !!nextRow && nextRow.sampleId !== rows[index].sampleId;
  }

  changedRowCount(event: MoistureHistoryEvent): number {
    return event.rows.filter((row) => row.changedInAction).length;
  }

  changedFieldCount(event: MoistureHistoryEvent): number {
    return event.rows.reduce(
      (total, row) =>
        total +
        row.tests.filter((test) => test.change !== 'Unchanged').length +
        (row.assignedTo.changed ? 1 : 0) +
        (row.status.changed ? 1 : 0),
      0,
    );
  }
}
